import React, { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import StatsTable from "./StatsTable";
import DropdownSelect from "./DropdownSelect";

const Statistics = ({
  mode,
  sidebar,
  seasons,
  teams = [],
  id = null,
  season_offset = 0,
  divisionName,
  clubName
}) => {
  const [season, setSeason] = useState(seasons[season_offset].value);
  const [team, setTeam] = useState(0);
  const [comp, setComp] = useState("all");
  const [teamName, setTeamName] = useState("all");
  const [seasonName, setSeasonName] = useState(seasons[season_offset].title);
  const [compName, setCompName] = useState("All Competitions");
  const [position, setPosition] = useState("skater");

  const handleTeamChange = (e) => {
    setTeam(e.target.value);

    const selectedValue = e.target.value;
    setTeamName(selectedValue.charAt(0).toUpperCase() + selectedValue.slice(1));

    teams.forEach((t) => {
      if (t.id.toString() === selectedValue) setTeamName(t.short);
    });
  };

  const handleCompChange = (e) => {
    setComp(e.target.value);

    const selectedValue = e.target.options[e.target.selectedIndex].text;
    setCompName(selectedValue);
  };

  const handlePositionChange = (e) => {
    setPosition(e.target.value);
  };

  const handleSeasonChange = (e) => {
    setSeason(e.target.value);
    setSeasonName(seasons[e.target.value].title)
  };

  const url = mode === "all" ? "all" : `${mode}/${id}`;

  const fetchStats = async ({ pageParam = 0 }) => {
    const res = await fetch(
      `${process.env.REACT_APP_BUIHA_API_URL}/statshub/${url}/?position=${position}&team=${team}&division=${comp}&offset=${pageParam}&season=${season}`
    );
    if (!res.ok) throw new Error("Network response was not ok");
    return res.json();
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    staleTime: Infinity,
    queryKey: ["statistics", mode, id, position, team, comp, season], // Add season here
    queryFn: fetchStats,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const loadingOptions = [{ value: "all", label: "Loading..." }];

  const seasonOptions = seasons.map((season) => ({
    value: season.value,
    label: season.title,
  }));

  const teamOptions = [
    { value: 0, label: "All Teams" },
    ...teams.map((team) => ({ value: team.id, label: team.short })),
  ];

  const compOptions = [
    { value: "all", label: "All Competitions" },
    { value: " CHK", label: "Checking" },
    { value: "N-CHK", label: "Non-Checking" },
    { value: "WOMENS", label: "Women's" },
    { value: "VAR", label: "Varsity" },
    { value: "CHAL", label: "Challenge" },
    { value: "CUP CHK", label: "Cup Checking" },
    { value: "CUP N-CHK", label: "Cup Non-Checking" },
    { value: "CUP WOMENS", label: "Cup Women's" },
    { value: "NATS CHK", label: "Nationals Checking" },
    { value: "NATS N-CHK", label: "Nationals Non-Checking" },
    { value: "NATS WOMENS", label: "Nationals Women's" },
    { value: "CUP CHK 1", label: "Cup Checking 1" },
    { value: "CUP CHK 2", label: "Cup Checking 2" },
    { value: "CUP N-CHK 1", label: "Cup Non-Checking 1" },
    { value: "CUP N-CHK 2", label: "Cup Non-Checking 2" },
    { value: "CUP N-CHK 3", label: "Cup Non-Checking 3" },
    { value: "NATS CHK T1", label: "Nationals Checking 1" },
    { value: "NATS CHK T2", label: "Nationals Checking 2" },
    { value: "NATS N-CHK T1", label: "Nationals Non-Checking 1" },
    { value: "NATS N-CHK T2", label: "Nationals Non-Checking 2" },
    { value: "NATS N-CHK T3", label: "Nationals Non-Checking 3" },
  ];

  const positionOptions = [
    { value: "skater", label: "Skater" },
    { value: "goalie", label: "Goalie" },
  ];

  if (error) {
    return <div>An error has occurred: {error.message}</div>;
  }

  const stats = data ? data.pages.flatMap((page) => page.stats || []) : [];

  return (
    <>
      <>
        <div className="w-full flex flex-row items-start justify-center lg:justify-between mb-2 sm:mb-3">
          <div className="hidden lg:block flex flex-col items-start justify-start">
            <h2 className="text-left text-2xl font-bold leading-tight">Statistics</h2>
            <p className="text-right font-light text-darken">
              Displaying {stats.length} {(compName.includes('All Competitions') ? "" : compName + " ")}{position} stats{" "}
              {mode === "all" ? "for the " + ((team > 0) ? teamName + " " : "") + seasonName.replace("-", "/") + " season" : ""}{" "}
              {mode === "division" && (team > 0) ? "for " + teamName : ""}{" "}
              {mode === "club" && (team > 0) ? "for " + teamName : ""}
            </p>
          </div>

          <div className="flex flex-row items-start justify-start">
            {seasons.length > 1 ? (
                <DropdownSelect
                    label="Season"
                    options={seasonOptions}
                    value={season}
                    onChange={handleSeasonChange}
                    className="mr-1 xs:mr-2"
                    disabled={isFetching}
                />
            ) : (
                <></>
            )}

            {id == null ? (
            <DropdownSelect
                label="Filter Competition"
                options={compOptions}
                value={comp}
                onChange={handleCompChange}
                className="mr-1 xs:mr-2"
                disabled={isFetching}
            />) : (
                <></>
            )}

            <DropdownSelect
                label="Filter Team"
                options={teamOptions}
                value={team}
                onChange={handleTeamChange}
                className="mr-1 xs:mr-2"
                disabled={isFetching}
            />

            <DropdownSelect
                label="Position"
                options={positionOptions}
                value={position}
                onChange={handlePositionChange}
                disabled={isFetching}
            />
          </div>
        </div>

        <div className="w-full mx-auto overflow-hidden">
          {isFetching && stats.length === 0 ? (
            <Loading />
          ) : (
            <>
              <StatsTable data={stats} position={position} />
              {hasNextPage && (
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="mt-4 p-2 bg-blue-500 text-white rounded disabled:opacity-50"
                >
                  {isFetchingNextPage ? "Loading more..." : "Load more"}
                </button>
              )}
              {stats.length === 0 ? (
                <p className="my-8 text-center dark-text text-darken">
                  No stats found
                </p>
              ) : null}
            </>
          )}
        </div>
      </>

      {sidebar && <div className="xl:block max-xl:hidden">{sidebar}</div>}
    </>
  );
};

export default Statistics;
